import { useAuth } from "@/hooks/useAuth/useAuth";
import {
  createFileRoute,
  Navigate,
  Outlet,
  useNavigate,
} from "@tanstack/react-router";
import { Image, MenuProps } from "antd";
import { useTheme } from "@base/hooks/useTheme/useTheme";
import VITE_ENV from "@/config/constants/vite-env";
import { useLocalApp } from "@base/hooks/useLocalApp/useLocalApp";

import logo from "@/assets/logo_white.png";
import logo_sm from "@/assets/logo_white.png"; // TODO: AGREGAR LOGO SM
import { LogoutOutlined, UserOutlined } from "@ant-design/icons";
import { RutaAutenticada } from "@/components/validators/RutaAutenticada";
import { AuthenticatedLayout } from "@base/components/layout/authenticated-layout/authenticated-layout";
import { ItemType, MenuItemType } from "antd/es/menu/interface";
import { useQuery } from "@tanstack/react-query";
import useHttp from "@base/hooks/useHttp/useHttp";
import { TipoDocumento } from "@/models/TipoDocumento.model";
import { Expediente } from "@/models/Expediente.model";
import { Empresa } from "@/models/Empresa.model";
import { calcularLlenadoPerfil } from "@/utils/evaluarEmpresaPerfil";
import { useMemo } from "react";
import { Entregable } from "@/models/Entregable.model";

export const Route = createFileRoute("/(auth)")({
  component: RouteComponent,
});

function RouteComponent() {
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();
  const http = useHttp();

  const qDocumentos = useQuery({
    queryKey: [TipoDocumento.ENDPOINTS.DEFAULT, http],
    queryFn: () =>
      http.get({
        endpoint: TipoDocumento.ENDPOINTS.DEFAULT,
        params: { tipo: "" },
      }),
  });

  const qExpedientesAll = useQuery({
    queryKey: [Expediente.ENDPOINTS.DEFAULT, "all", http],
    queryFn: () =>
      http.get({
        endpoint: Expediente.ENDPOINTS.DEFAULT,
        params: { expand: "media" },
      }),
  });

  const qEmpresa = useQuery({
    queryKey: [Empresa.ENDPOINTS.DEFAULT, usuario?.id, http],
    queryFn: () =>
      http.get({
        endpoint: Empresa.ENDPOINTS.DEFAULT,
        params: {
          expand: "grupos,empresaGrupos,empresaGrupos.grupo",
        },
      }),
  });

  const qEntregas = useQuery({
    queryKey: [Entregable.ENDPOINTS.BADGE, http],
    queryFn: () =>
      http.get({
        endpoint: Entregable.ENDPOINTS.BADGE,
      }),
  });

  const documentos: TipoDocumento[] = qDocumentos.data?.resultado ?? [];
  const expedientesAll: Expediente[] = qExpedientesAll.data?.resultado ?? [];
  const totalEntregas: number = qEntregas.data?.detalle ?? 0;

  const totalTipos = documentos.length;
  const atendidosCount = documentos.filter((doc) =>
    expedientesAll.some((exp) => String(exp.idTipo) === String(doc.id)),
  ).length;

  const isExpedienteCompleto = totalTipos > 0 && atendidosCount >= totalTipos;
  const badgeTextExpediente = isExpedienteCompleto
    ? "100%"
    : `${atendidosCount}/${totalTipos}`;
  const badgeColorExpediente = isExpedienteCompleto ? "#52c41a" : "#ff4d4f";

  const rawEmpresa =
    qEmpresa.data?.resultado ?? qEmpresa.data?.detalle ?? qEmpresa.data;
  const empresaData = Array.isArray(rawEmpresa) ? rawEmpresa[0] : rawEmpresa;
  const currentEmpresaData = {
    ...empresaData,
    correoUsuario: empresaData?.correoUsuario || usuario?.correoUsuario,
  };
  const {
    porcentaje: porcentajePerfil,
    completados: camposCompletadosPerfil,
    totales: totalCamposPerfil,
  } = calcularLlenadoPerfil(currentEmpresaData);

  const isPerfilCompleto = porcentajePerfil === 100;
  const badgeTextPerfil = isPerfilCompleto
    ? "100%"
    : `${camposCompletadosPerfil}/${totalCamposPerfil}`;
  const badgeColorPerfil = isPerfilCompleto ? "#52c41a" : "#ff4d4f";

  const userNavMenuItems: MenuProps = {
    items: [
      {
        key: "1",
        label: (
          <span className="flex gap-2 text-sm">
            <UserOutlined /> Mi Perfil
          </span>
        ),
        onClick: () => navigate({ to: "/perfil" }),
      },
      {
        key: "2",
        danger: true,
        label: (
          <span style={{ fontSize: "14px" }}>
            {" "}
            <LogoutOutlined /> Cerrar sesión
          </span>
        ),
        onClick: () => {
          logout();
        },
      },
    ],
  };

  const { theme } = useTheme();
  const { current_menu, breadcrumbs, env, activeKeys } = useLocalApp();

  const menuWithBadges = useMemo(
    () =>
      current_menu?.map((item: any) => {
        if (item.key === "/expediente") {
          return {
            ...item,
            badge: badgeTextExpediente,
            badgeColor: badgeColorExpediente,
          };
        }
        if (item.key === "/perfil") {
          return {
            ...item,
            badge: badgeTextPerfil,
            badgeColor: badgeColorPerfil,
          };
        }
        if (item.key === "/entregas") {
          return {
            ...item,
            badge: totalEntregas,
            badgeColor: badgeColorPerfil,
          };
        }
        return item;
      }),
    [
      current_menu,
      badgeTextExpediente,
      badgeColorExpediente,
      badgeTextPerfil,
      badgeColorPerfil,
      totalEntregas,
    ],
  );

  const LogoRender = (collapsed: boolean) => {
    return (
      <Image
        src={!collapsed ? logo : logo_sm}
        className={"w-full"}
        alt={`logo de ${env.APP_NAME}`}
        preview={false}
      />
    );
  };

  const handleInvalid = useMemo(() => <Navigate to="/login" />, []);

  return (
    <RutaAutenticada handleInvalid={handleInvalid}>
      <AuthenticatedLayout
        usuario={usuario}
        theme={theme}
        sidebarMenuItems={menuWithBadges as ItemType<MenuItemType>[]}
        userNavMenuItems={userNavMenuItems}
        breadcrumbItems={breadcrumbs}
        appVersion={VITE_ENV.APP_VERSION ?? "0.0.1"}
        ocultarLayout={false}
        ocultarBreadcrumb={false}
        logoRender={LogoRender}
        activeKeys={activeKeys}
      >
        <Outlet />
      </AuthenticatedLayout>
    </RutaAutenticada>
  );
}
